<script setup>
import { useI18n } from 'vue-i18n'
import { formatDateTime, formatDuration, formatOptionalInt } from '../llm-format.js'
import ConversationMessage from './ConversationMessage.vue'

defineProps({
  calls: { type: Array, required: true },
  requestOpen: { type: Boolean, default: false },
})

const { t } = useI18n()

function responseMessage(call, index) {
  return {
    index,
    role: 'assistant',
    content_type: 'json',
    content: call.response,
  }
}
</script>

<template>
  <section class="conversation-calls">
    <ol>
      <li v-for="(call, index) in calls" :key="call.id">
        <ConversationMessage :message="responseMessage(call, index)" />

        <details class="conversation-calls__context" :open="requestOpen">
          <summary>{{ t('visualGameAdmin.llm.conversations.detail.requestContext') }}</summary>
          <div class="conversation-calls__context-body">
            <details class="conversation-calls__metadata">
              <summary>{{ t('visualGameAdmin.llm.conversations.detail.callDetails') }}</summary>
              <div>
                <span>{{ formatDateTime(call.created_at) }} · {{ call.model }} · {{ formatDuration(call.duration_ms) }}</span>
                <span>
                  {{ t('visualGameAdmin.llm.conversations.detail.callTokens', {
                    input: formatOptionalInt(call.prompt_tokens),
                    output: formatOptionalInt(call.completion_tokens),
                    cached: formatOptionalInt(call.cached_tokens),
                    cacheWrite: formatOptionalInt(call.cache_write_tokens),
                  }) }}
                </span>
                <span class="vga-mono">
                  {{ call.llm_request_id === null
                    ? t('visualGameAdmin.llm.conversations.detail.noRequestId')
                    : `${t('visualGameAdmin.llm.conversations.detail.requestId')}: ${call.llm_request_id}` }}
                </span>
              </div>
            </details>
            <ol class="conversation-calls__messages">
              <ConversationMessage
                v-for="message in call.request_context"
                :key="message.index"
                :message="message"
                collapsible
              />
            </ol>
          </div>
        </details>
      </li>
    </ol>
  </section>
</template>

<style scoped>
.conversation-calls ol {
  display: grid;
  gap: var(--drama-space-md);
  margin: 0;
  padding: 0;
  list-style: none;
}

.conversation-calls li {
  display: grid;
  gap: var(--drama-space-md);
}

.conversation-calls__context {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.conversation-calls__context > summary {
  cursor: pointer;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  font-weight: var(--drama-font-weight-medium);
}

.conversation-calls__context-body {
  display: grid;
  gap: var(--drama-space-md);
  margin-top: var(--drama-space-md);
}

.conversation-calls__metadata {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.conversation-calls__metadata summary {
  cursor: pointer;
}

.conversation-calls__metadata > div {
  display: grid;
  gap: var(--drama-space-sm);
  margin-top: var(--drama-space-sm);
  padding: var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

.conversation-calls__metadata span {
  overflow-wrap: anywhere;
}

.conversation-calls__messages {
  display: grid;
  gap: var(--drama-space-lg);
  margin: 0;
  padding: 0;
  list-style: none;
}
</style>
